import React, { useRef, useState, useEffect, MediaHTMLAttributes } from 'react'

interface IProps {
  src: string
  type: string
  width?: number | string
  height?: number | string
  className?: string
  props?: MediaHTMLAttributes<HTMLVideoElement>
}

const loadQueue: Array<() => void> = []
let activeLoads = 0
const maxConcurrentLoads = 3

const LazyVideo = ({ src, type, width, height, className, props }: IProps) => {
  const [inView, setInView] = useState(false)
  const videoRef = useRef<HTMLDivElement | null>(null)

  const loadVideo = () => {
    if (activeLoads < maxConcurrentLoads) {
      activeLoads++
      setInView(true)
    } else {
      loadQueue.push(() => setInView(true))
    }
  }

  const handleLoadQueue = () => {
    if (activeLoads > 0) {
      activeLoads--
      if (loadQueue.length > 0) {
        const nextLoad = loadQueue.shift()
        nextLoad && nextLoad()
      }
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadVideo()
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.1 }
    )

    const currentRef = videoRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (inView) {
      handleLoadQueue()
    }
  }, [inView])

  return (
    <div
      ref={videoRef}
      className={className}
      style={{
        width: width || '100%',
        height: height || 'auto',
        position: 'relative'
      }}
    >
      {inView ? (
        <video {...props} muted width="100%" height="100%">
          <source src={src} type={type} />
        </video>
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'white'
          }}
        />
      )}
    </div>
  )
}

export default LazyVideo
